import { encodeQueryParams } from "@hex/common";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DEFAULT_PLAIN_CODE_PROJECT_OPEN_VIEWS,
  PLAIN_CODE_PROJECT_VIEW,
  type PlainCodeProjectView,
} from "./plainCodeProjectViews";

export type PlainCodeProjectBranchQueryParams = {
  threadId?: string;
  s1?: PlainCodeProjectView;
  s2?: PlainCodeProjectView;
};

export function usePlainCodeProjectOpenViews(): {
  changeView: (replace: boolean, view: PlainCodeProjectView) => void;
  openPreviewTab: () => void;
  openViews: PlainCodeProjectView[];
} {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const { needsRepair, paneState } = useMemo(
    () => parsePlainCodeProjectPaneStateFromSearchParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    if (!needsRepair) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    setPlainCodeProjectSlotParams(nextParams, paneState);
    void navigate({ search: nextParams.toString() }, { replace: true });
  }, [location.search, navigate, needsRepair, paneState]);

  const openViews = useMemo(
    () => openViewsFromPaneState(paneState),
    [paneState],
  );

  const changeView = useCallback(
    (replace: boolean, view: PlainCodeProjectView) => {
      const nextPaneState = getNextPlainCodeProjectPaneState(
        paneState,
        view,
        replace,
      );
      const nextParams = new URLSearchParams(location.search);
      setPlainCodeProjectSlotParams(nextParams, nextPaneState);
      void navigate({ search: nextParams.toString() }, { replace: true });
    },
    [location.search, navigate, paneState],
  );

  const openPreviewTab = useCallback(() => {
    const nextParams = new URLSearchParams(location.search);
    setPlainCodeProjectSlotParams(nextParams, {
      slot1: PLAIN_CODE_PROJECT_VIEW.preview,
      slot2: null,
    });
    void navigate({ search: nextParams.toString() }, { replace: true });
  }, [location.search, navigate]);

  return useMemo(
    () => ({ changeView, openPreviewTab, openViews }),
    [changeView, openPreviewTab, openViews],
  );
}

export function plainCodeProjectBranchQueryString(
  queryParams?: PlainCodeProjectBranchQueryParams,
): string {
  const params = new URLSearchParams();
  appendPlainCodeProjectBranchQueryParams(params, queryParams);
  return encodeQueryParams(Object.fromEntries(params.entries()));
}

interface PlainCodeProjectPaneState {
  slot1: PlainCodeProjectView;
  slot2: PlainCodeProjectView | null;
}

const PLAIN_CODE_SLOT1_QUERY_PARAM = "s1";
const PLAIN_CODE_SLOT2_QUERY_PARAM = "s2";

const PLAIN_CODE_PROJECT_VIEW_VALUES: ReadonlySet<string> = new Set(
  Object.values(PLAIN_CODE_PROJECT_VIEW),
);

function isPlainCodeProjectView(value: string): value is PlainCodeProjectView {
  return PLAIN_CODE_PROJECT_VIEW_VALUES.has(value);
}

function getDefaultPlainCodeProjectPaneState(): PlainCodeProjectPaneState {
  const [slot1] = DEFAULT_PLAIN_CODE_PROJECT_OPEN_VIEWS;
  return { slot1, slot2: null };
}

function isDefaultPlainCodeProjectPaneState(
  state: PlainCodeProjectPaneState,
): boolean {
  const defaultState = getDefaultPlainCodeProjectPaneState();
  return (
    state.slot1 === defaultState.slot1 && state.slot2 === defaultState.slot2
  );
}

function parsePlainCodeProjectView(
  value: string | null,
): PlainCodeProjectView | null {
  if (value == null || !isPlainCodeProjectView(value)) {
    return null;
  }
  return value;
}

function openViewsFromPaneState(
  state: PlainCodeProjectPaneState,
): PlainCodeProjectView[] {
  if (state.slot2 != null) {
    return [state.slot1, state.slot2];
  }
  return [state.slot1];
}

function paneStateFromOpenViews(
  views: readonly PlainCodeProjectView[],
): PlainCodeProjectPaneState {
  const defaultState = getDefaultPlainCodeProjectPaneState();
  const slot1 = views[0] ?? defaultState.slot1;
  const slot2 = views[1] ?? null;
  return { slot1, slot2 };
}

function parsePlainCodeProjectPaneStateFromSearchParams(
  params: URLSearchParams,
): { paneState: PlainCodeProjectPaneState; needsRepair: boolean } {
  const defaultState = getDefaultPlainCodeProjectPaneState();
  const rawSlot1 = params.get(PLAIN_CODE_SLOT1_QUERY_PARAM);
  const rawSlot2 = params.get(PLAIN_CODE_SLOT2_QUERY_PARAM);

  if (rawSlot1 == null && rawSlot2 == null) {
    return { paneState: defaultState, needsRepair: false };
  }

  const parsedSlot1 = parsePlainCodeProjectView(rawSlot1);
  const parsedSlot2 = parsePlainCodeProjectView(rawSlot2);

  if (rawSlot1 == null && rawSlot2 != null) {
    return { paneState: defaultState, needsRepair: true };
  }

  if (parsedSlot1 == null) {
    return { paneState: defaultState, needsRepair: true };
  }

  if (rawSlot2 == null) {
    return {
      paneState: { slot1: parsedSlot1, slot2: null },
      needsRepair: false,
    };
  }

  if (parsedSlot2 == null || parsedSlot2 === parsedSlot1) {
    return {
      paneState: { slot1: parsedSlot1, slot2: null },
      needsRepair: true,
    };
  }

  return {
    paneState: { slot1: parsedSlot1, slot2: parsedSlot2 },
    needsRepair: false,
  };
}

function setPlainCodeProjectSlotParams(
  params: URLSearchParams,
  paneState: PlainCodeProjectPaneState,
): void {
  if (isDefaultPlainCodeProjectPaneState(paneState)) {
    params.delete(PLAIN_CODE_SLOT1_QUERY_PARAM);
    params.delete(PLAIN_CODE_SLOT2_QUERY_PARAM);
    return;
  }

  params.set(PLAIN_CODE_SLOT1_QUERY_PARAM, paneState.slot1);
  if (paneState.slot2 != null) {
    params.set(PLAIN_CODE_SLOT2_QUERY_PARAM, paneState.slot2);
  } else {
    params.delete(PLAIN_CODE_SLOT2_QUERY_PARAM);
  }
}

function getNextPlainCodeProjectPaneState(
  current: PlainCodeProjectPaneState,
  view: PlainCodeProjectView,
  replace: boolean,
): PlainCodeProjectPaneState {
  const openViews = openViewsFromPaneState(current);

  if (replace) {
    return { slot1: view, slot2: null };
  }

  if (openViews.includes(view)) {
    if (openViews.length > 1) {
      return paneStateFromOpenViews(openViews.filter((v) => v !== view));
    }
    return current;
  }

  return paneStateFromOpenViews([...openViews, view]);
}

function appendPlainCodeProjectBranchQueryParams(
  params: URLSearchParams,
  queryParams?: PlainCodeProjectBranchQueryParams,
): void {
  if (queryParams == null) {
    return;
  }
  if (queryParams.threadId != null) {
    params.set("threadId", queryParams.threadId);
  }
  if (queryParams.s1 != null || queryParams.s2 != null) {
    setPlainCodeProjectSlotParams(params, {
      slot1: queryParams.s1 ?? getDefaultPlainCodeProjectPaneState().slot1,
      slot2: queryParams.s2 ?? null,
    });
  }
}
